import React, { useCallback, useEffect, useState } from "react";
import { applyInternalJob, listApplications, listPostings } from "../../api/recruitment";
import { getMyEmployee } from "../../api/employees";
import { validateAttachmentFile } from "../../api/attachments";
import JobApplyForm, { EMPTY_APPLY_FORM } from "../careers/JobApplyForm";
import "../hr/HrRecruitment.css";
import "../careers/PublicCareers.css";

function apiError(err, fallback) {
  const message = err?.response?.data?.message;
  if (Array.isArray(message)) return message.join("\n");
  if (typeof message === "string" && message.trim()) return message;
  return err?.message || fallback;
}

function unwrap(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
}

function toDateInput(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
}

function prefillFromEmployee(emp) {
  return {
    ...EMPTY_APPLY_FORM,
    fullName: emp.fullName || "",
    email: emp.email || "",
    phone: emp.phone || "",
    nationalId: emp.nationalId || "",
    dateOfBirth: toDateInput(emp.dateOfBirth || emp.birthDate),
  };
}

export default function InternalJobs() {
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [postings, setPostings] = useState([]);
  const [applications, setApplications] = useState([]);
  const [activePostingId, setActivePostingId] = useState(null);
  const [form, setForm] = useState(EMPTY_APPLY_FORM);
  const [cvFile, setCvFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [prefillReady, setPrefillReady] = useState(false);
  const [employeeProfile, setEmployeeProfile] = useState(null);

  const load = useCallback(async () => {
    setError("");
    try {
      const [posts, apps] = await Promise.all([
        listPostings({ openOnly: true }),
        listApplications(),
      ]);
      setPostings(unwrap(posts).filter((p) => p.status === "OPEN"));
      setApplications(unwrap(apps));
    } catch (err) {
      setError(apiError(err, "تعذر تحميل الوظائف الداخلية"));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const emp = await getMyEmployee();
        if (cancelled || !emp) return;
        setEmployeeProfile(emp);
        setForm(prefillFromEmployee(emp));
      } catch {
        // prefill optional
      } finally {
        if (!cancelled) setPrefillReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const appliedPostingIds = new Set(applications.map((a) => a.postingId || a.posting?.id));

  function openApplyForm(postingId) {
    setActivePostingId(postingId);
    setError("");
    setMessage("");
    setCvFile(null);
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setError("");
    const fileError = validateAttachmentFile(cvFile);
    if (fileError) {
      setError(fileError);
      return;
    }
    setSubmitting(true);
    try {
      await applyInternalJob({ postingId: activePostingId, ...form }, cvFile);
      setMessage("تم تقديمك بنجاح");
      setActivePostingId(null);
      setForm(employeeProfile ? prefillFromEmployee(employeeProfile) : EMPTY_APPLY_FORM);
      setCvFile(null);
      await load();
    } catch (err) {
      setError(apiError(err, "تعذر التقديم"));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="hr-recruitment">
      <div className="hr-recruitment__header">
        <div>
          <h1 className="hr-recruitment__title">وظائف داخلية</h1>
          <p className="hr-recruitment__subtitle">
            املأ بياناتك كاملة ثم قدّم على الوظيفة — جميع الحقول إجبارية
          </p>
        </div>
      </div>
      {error && !activePostingId ? <div className="hr-recruitment__error">{error}</div> : null}
      {message ? <div className="hr-recruitment__muted">{message}</div> : null}
      {!prefillReady && !activePostingId ? (
        <div className="hr-recruitment__muted">جارٍ تحميل بياناتك...</div>
      ) : null}

      <div className="hr-recruitment__kanban">
        {postings.map((posting) => (
          <div key={posting.id} className="hr-recruitment__card">
            <strong>{posting.title}</strong>
            <div className="hr-recruitment__muted">{posting.department?.name || ""}</div>
            <p>{posting.description || "لا يوجد وصف إضافي."}</p>
            {appliedPostingIds.has(posting.id) ? (
              <span className="hr-recruitment__muted">تم التقديم</span>
            ) : activePostingId === posting.id ? (
              <>
                <JobApplyForm
                  form={form}
                  setForm={setForm}
                  cvFile={cvFile}
                  setCvFile={setCvFile}
                  onSubmit={handleSubmit}
                  submitting={submitting}
                  error={error}
                  submitLabel="إرسال التقديم"
                />
                <button
                  type="button"
                  className="hr-recruitment__btn hr-recruitment__btn--ghost"
                  style={{ marginTop: 8 }}
                  onClick={() => setActivePostingId(null)}
                >
                  إلغاء
                </button>
              </>
            ) : (
              <button
                type="button"
                className="hr-recruitment__btn"
                onClick={() => openApplyForm(posting.id)}
              >
                تقديم
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
